import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InstallShareCard } from "@/components/InstallShare";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircleHeart, NotebookPen, ShieldCheck, Users, Sparkles, Trash2, Star } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwcGVvcGxlJTIwc21pbGluZ3xlbnwwfHx8fDE3ODIyMTI0MDF8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: MessageCircleHeart, title: "Jungian Companion", text: "A warm guide rooted in Carl Jung's depth psychology — shadow, archetypes, individuation — adapted to your culture and age." },
  { icon: NotebookPen, title: "Monitored Journaling", text: "Write freely. Aurea gently reflects back and quietly watches for moments where you may need more support." },
  { icon: ShieldCheck, title: "Compassionate Safety Net", text: "If a red-flag moment arises, calming crisis resources appear and your emergency contact is quietly notified." },
  { icon: Users, title: "Find a Therapist", text: "A culturally diverse directory of real-world therapists, with one-tap appointment requests near you." },
  { icon: Sparkles, title: "Depth Tools", text: "Shadow work, active imagination, anger cooling, parenting repair, grounding breath, and more." },
  { icon: Trash2, title: "Delete Anytime", text: "Your peace of mind matters. Erase all of your data permanently, in one tap, whenever you choose." },
];

const Landing = () => {
  const [feedback, setFeedback] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadFeedback = () => api.get("/feedback").then(({ data }) => setFeedback(data)).catch(() => {});
  useEffect(() => { loadFeedback(); }, []);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.info("Please write a short message."); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", form);
      setForm({ name: "", rating: 5, message: "" });
      await loadFeedback();
      toast.success("Thank you for sharing — your words matter.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen text-ink">
      <header className="flex items-center justify-between px-6 sm:px-10 py-6 max-w-7xl mx-auto">
        <span className="font-serif text-3xl text-brand tracking-tight">Aurea</span>
        <div className="flex items-center gap-3">
          <Link to="/login"><Button variant="ghost" className="rounded-full text-ink-2" data-testid="nav-login">Sign in</Button></Link>
          <Link to="/register"><Button className="rounded-full bg-brand hover:bg-brand-hover text-linen" data-testid="nav-get-started">Begin</Button></Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-12 items-center pt-10 pb-20">
        <div className="animate-fade-up">
          <p className="text-accent font-medium tracking-wide uppercase text-xs mb-5">Depth psychology · culturally safe · for every age</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-ink tracking-tight">
            A quiet place to <span className="italic text-brand">meet yourself.</span>
          </h1>
          <p className="mt-7 text-lg text-ink-2 leading-relaxed max-w-xl">
            Aurea is an AI wellbeing companion grounded in Carl Jung's insights — offering trauma-aware reflection,
            journaling, and a gentle safety net. Honoring your name, age, ethnicity and culture, every step of the way.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/register"><Button className="rounded-full bg-brand hover:bg-brand-hover text-linen px-8 py-6 text-base" data-testid="hero-cta">Start your journey</Button></Link>
            <Link to="/login"><Button variant="outline" className="rounded-full border-brand text-brand hover:bg-linen-2 px-8 py-6 text-base" data-testid="hero-signin">I have an account</Button></Link>
          </div>
          <p className="mt-6 text-xs text-ink-mute max-w-md">
            Aurea supports self-awareness and is not a substitute for professional or emergency care.
          </p>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="absolute -inset-4 bg-calm/60 rounded-[2.5rem] -rotate-2" />
          <img src={HERO} alt="A diverse community" className="relative rounded-[2.5rem] object-cover w-full h-[420px] sm:h-[520px] shadow-sm" />
        </div>
      </section>

      <section className="bg-linen-2 py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <h2 className="font-serif text-4xl sm:text-5xl text-ink text-center mb-4">Care that holds all of you</h2>
          <p className="text-center text-ink-2 max-w-2xl mx-auto mb-14">Tools designed to feel like a sanctuary, not a clinic.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-linen rounded-3xl p-8 border border-linen-3 hover:-translate-y-0.5 transition-transform duration-300" data-testid={`feature-${i}`}>
                <span className="w-12 h-12 rounded-2xl bg-calm flex items-center justify-center mb-5">
                  <f.icon className="w-6 h-6 text-brand" />
                </span>
                <h3 className="font-serif text-2xl text-ink mb-2">{f.title}</h3>
                <p className="text-ink-2 text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-20">
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          <div className="flex">
            <div className="w-full"><InstallShareCard /></div>
          </div>
          <div className="bg-brand text-linen rounded-3xl p-8 flex flex-col justify-center">
            <h3 className="font-serif text-3xl">Free, private, and yours</h3>
            <p className="text-linen/85 mt-3 leading-relaxed">Aurea works right in your browser — no app store needed. Add it to your home screen and open it like any app, anytime you need a quiet moment.</p>
          </div>
        </div>
      </section>

      <section id="feedback" className="bg-linen-2 py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <h2 className="font-serif text-4xl sm:text-5xl text-ink text-center mb-3">From our community</h2>
          <p className="text-center text-ink-2 max-w-2xl mx-auto mb-12">Share how Aurea has touched your journey — your words help others feel less alone.</p>

          <form onSubmit={submitFeedback} className="bg-linen rounded-3xl border border-linen-3 p-6 sm:p-8 mb-10 max-w-2xl mx-auto" data-testid="feedback-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name (optional)" className="rounded-2xl bg-white border-linen-3 h-12" data-testid="feedback-name" />
              <div className="flex items-center gap-2" data-testid="feedback-rating">
                <span className="text-sm text-ink-2">Rating:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`rating-${n}`} aria-label={`rate ${n}`}>
                    <Star className={`w-6 h-6 ${n <= form.rating ? "fill-accent text-accent" : "text-linen-3"}`} />
                  </button>
                ))}
              </div>
            </div>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What would you like to share?" className="rounded-2xl bg-white border-linen-3 min-h-[100px] mt-4" data-testid="feedback-message" />
            <Button type="submit" disabled={submitting} className="rounded-full bg-brand hover:bg-brand-hover text-linen mt-4 px-8" data-testid="feedback-submit">{submitting ? "Sharing…" : "Share feedback"}</Button>
          </form>

          {feedback.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="feedback-wall">
              {feedback.map((f) => (
                <div key={f.id} className="bg-linen rounded-3xl border border-linen-3 p-6" data-testid="feedback-item">
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < f.rating ? "fill-accent text-accent" : "text-linen-3"}`} />)}
                  </div>
                  <p className="text-ink-2 leading-relaxed text-[15px]">“{f.message}”</p>
                  <p className="font-serif text-lg text-brand mt-3">— {f.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="font-serif text-3xl sm:text-4xl italic text-brand leading-snug">
          “Until you make the unconscious conscious, it will direct your life and you will call it fate.”
        </p>
        <p className="mt-4 text-ink-mute">— Carl Gustav Jung</p>
        <div className="mt-10">
          <Link to="/register"><Button className="rounded-full bg-accent hover:bg-accent-hover text-linen px-8 py-6 text-base" data-testid="footer-cta">Create your sanctuary</Button></Link>
        </div>
      </section>

      <footer className="border-t border-linen-3 py-8 text-center text-sm text-ink-mute">
        <p>Aurea · A wellbeing companion. In crisis? Call your local emergency number.</p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <Link to="/terms" className="hover:text-brand transition-colors" data-testid="footer-terms">Terms of Service</Link>
          <span className="text-linen-3">·</span>
          <Link to="/privacy" className="hover:text-brand transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
        </div>
        <p className="mt-3">© {new Date().getFullYear()} Aurea · Damian Alan Moses. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Landing;
